import { useMutation } from '@tanstack/react-query';
import {
  PatchCourseMappingRequestBody,
  PatchCourseMappingResponseBody,
  PostCourseMappingRequestBody,
  PostCourseMappingResponseBody,
} from '@ab/ams-school-api/types/private/v1';
import { config } from '~/config';
import { hasKey } from '~/utils';
import { useRequest } from '~/lib/useRequest';

export type UpdateCourseMappingParams = {
  courseMappingId: string;
  equivalentCourseId: string;
  sourceCourseName?: string;
};

export type CreateCourseMappingParams = {
  sourceCourseName: string;
  equivalentCourseId: string;
  sourceCountryCode?: string;
  sourceEducationLevel?: string;
  sourceInstitutionName?: string;
};

export function useUpdateCourseMapping(applicationId: string) {
  const request = useRequest();

  const { isLoading, error, mutateAsync } = useMutation({
    mutationKey: ['updateCourseMapping', applicationId],
    mutationFn: async (params: UpdateCourseMappingParams) => {
      const response = await request<PatchCourseMappingResponseBody>(
        `${config.apiHost}/private/v1/course_mappings/${params.courseMappingId}`,
        {
          method: 'PATCH',
          body: {
            data: {
              id: params.courseMappingId,
              type: 'course_mapping',
              attributes: {
                equivalentCourse: params.equivalentCourseId,
                sourceCourseName: params.sourceCourseName,
              },
            },
          } as PatchCourseMappingRequestBody,
          isExpectedResponse: isPatchExpectedResponse,
        }
      );

      return response.data;
    },
  });

  return {
    updateCourseMapping: mutateAsync,
    isUpdatingCourseMapping: isLoading,
    updateCourseMappingError: error,
  };
}

export function useCreateCourseMapping(applicationId: string) {
  const request = useRequest();

  const { isLoading, error, mutateAsync } = useMutation({
    mutationKey: ['createCourseMapping', applicationId],
    mutationFn: async (params: CreateCourseMappingParams) => {
      const response = await request<PostCourseMappingResponseBody>(`${config.apiHost}/private/v1/course_mappings`, {
        method: 'POST',
        body: {
          data: {
            type: 'course_mapping',
            attributes: {
              sourceCourseName: params.sourceCourseName,
              equivalentCourse: params.equivalentCourseId,
              sourceCountryCode: params.sourceCountryCode,
              sourceInstitutionName: params.sourceInstitutionName,
            },
          },
        } as PostCourseMappingRequestBody,
        isExpectedResponse: isPostExpectedResponse,
      });

      return response.data;
    },
  });

  return {
    createCourseMapping: mutateAsync,
    isCreatingCourseMapping: isLoading,
    createCourseMappingError: error,
  };
}

function isPatchExpectedResponse(res: unknown): res is PatchCourseMappingResponseBody {
  return Boolean(res && typeof res === 'object' && hasKey('data', res));
}

function isPostExpectedResponse(res: unknown): res is PostCourseMappingResponseBody {
  return Boolean(res && typeof res === 'object' && hasKey('data', res));
}
